import React, { createContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

export const AuthContext = createContext(null);

const ROLE_ALIAS_MAP = {
  ENGINEER: 'PROJECT_ENGINEER',
};

const extractRoles = (userData) => {
  if (!userData) {
    return [];
  }

  const roleNames = (userData.roles || [])
    .map((entry) => {
      if (typeof entry === 'string') {
        return entry;
      }

      return entry.roleType || entry.role?.name;
    })
    .filter(Boolean);

  return [...new Set(roleNames)];
};

const normalizeUser = (userData) => {
  if (!userData) {
    return null;
  }

  const roles = extractRoles(userData);
  const primaryRole = userData.isSuperAdmin ? 'SUPER_ADMIN' : roles[0] || null;

  return {
    ...userData,
    roles,
    primaryRole,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const persistAuth = useCallback((userData, token) => {
    const normalizedUser = normalizeUser(userData);

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
    localStorage.setItem('companyId', normalizedUser?.companyId || '');

    setUser(normalizedUser);
    setIsAuthenticated(true);

    return normalizedUser;
  }, []);

  useEffect(() => {
    const restoreAuth = () => {
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (storedUser && token) {
        try {
          const parsed = JSON.parse(storedUser);
          setUser(normalizeUser(parsed));
          setIsAuthenticated(true);
          return;
        } catch (error) {
          console.error('Error parsing stored user:', error);
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('companyId');
        }
      }

      setUser(null);
      setIsAuthenticated(false);
    };

    restoreAuth();
    setLoading(false);
  }, []);

  useEffect(() => {
    const syncAuthAcrossTabs = (event) => {
      if (!['token', 'user', 'companyId'].includes(event.key)) {
        return;
      }

      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');

      if (storedUser && token) {
        try {
          setUser(normalizeUser(JSON.parse(storedUser)));
          setIsAuthenticated(true);
          return;
        } catch (error) {
          console.error('Error syncing stored user:', error);
        }
      }

      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    window.addEventListener('storage', syncAuthAcrossTabs);
    return () => window.removeEventListener('storage', syncAuthAcrossTabs);
  }, []);

  const login = useCallback(async (email) => {
    try {
      const response = await authAPI.login({ email });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed',
      };
    }
  }, []);

  const registerCompanyAdmin = useCallback(async (data) => {
    try {
      const response = await authAPI.registerCompanyAdmin(data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Company registration error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Company registration failed',
      };
    }
  }, []);

  const signup = useCallback(async (data) => {
    try {
      const response = await authAPI.signup(data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Signup error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Signup failed',
      };
    }
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    try {
      const response = await authAPI.verifyOtp({ email, otp });
      const { access_token, token, user: userData } = response.data;
      const authToken = access_token || token;

      const normalizedUser = persistAuth(userData, authToken);

      return { success: true, data: normalizedUser };
    } catch (error) {
      console.error('OTP verification error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'OTP verification failed',
      };
    }
  }, [persistAuth]);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('companyId');
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  const hasRole = useCallback((allowedRoles = []) => {
    if (!user) {
      return false;
    }

    if (user.isSuperAdmin) {
      return true;
    }

    const normalizedAllowedRoles = allowedRoles.flatMap((role) =>
      [role, ROLE_ALIAS_MAP[role]].filter(Boolean),
    );

    return normalizedAllowedRoles.some((role) => user.roles.includes(role));
  }, [user]);

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    registerCompanyAdmin,
    verifyOtp,
    logout,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
